// src/redux/thunks/cartThunks.js
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import {
	fetchCartStart,
	fetchCartSuccess,
	fetchCartFailure,
	addToCartSuccess,
	removeFromCartSuccess,
	clearCartSuccess,
	updateCartItemQuantity,
} from "../slices/cartSlice";
import { logout } from "../slices/authSlice";

// Helper function to check authentication
const checkAuth = () => {
	const token = Cookies.get("ecom_user_token");
	return !!token; // Returns true if token exists
};

// Common function to handle auth errors
const handleAuthError = (dispatch) => {
	toast.error("Your session has expired. Please login again.", {
		position: "top-right",
		autoClose: 3000,
		hideProgressBar: false,
		closeOnClick: true,
		pauseOnHover: true,
		draggable: true,
	});

	Cookies.remove("ecom_user_token");
	dispatch(logout());

	return false;
};

export const fetchCart = () => async (dispatch) => {
	// ✅ FIRST CHECK: Agar user authenticated nahi hai, to API call hi na karein
	if (!checkAuth()) {
		dispatch(fetchCartFailure("User not authenticated. Please login."));
		return;
	}

	try {
		dispatch(fetchCartStart());

		const token = Cookies.get("ecom_user_token");
		const response = await axios.get(
			`${process.env.REACT_APP_BACKEND_URL}/api/buyer/get-cart`,
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		);

		const cartItems = response.data.cart.items || [];

		let totalItems = 0;
		let totalPrice = 0;

		const formattedItems = cartItems.map((item) => {
			const total = item.price * item.quantity;
			totalItems += item.quantity;
			totalPrice += total;

			return {
				productId: item?.productId._id,
				variantId: item?.variantId || null,
				image: item?.image,
				sellerId: item?.sellerId,
				name: item?.productId?.name,
				variantName: item?.variantId?.name || null,
				quantity: item?.quantity,
				price: item?.price,
				total,
			};
		});

		const formattedCart = {
			items: formattedItems,
			loading: false,
			error: null,
			totalItems,
			totalPrice,
		};

		dispatch(fetchCartSuccess(formattedCart));
	} catch (error) {
		// Check for authentication errors
		if (
			error.response &&
			(error.response.status === 401 || error.response.status === 403)
		) {
			handleAuthError(dispatch);
			return;
		}

		if (error.response && error.response.status === 404) {
			// If cart not found (404), dispatch success with empty cart
			const emptyCart = {
				items: [],
				loading: false,
				error: null,
				totalItems: 0,
				totalPrice: 0,
			};
			dispatch(fetchCartSuccess(emptyCart));
		} else {
			// For other errors, dispatch failure
			dispatch(fetchCartFailure(error.message));
		}
	}
};

export const addToCart =
	({ productId, variantId, quantity }) =>
	async (dispatch) => {
		// ✅ FIRST CHECK: Agar user authenticated nahi hai, to API call hi na karein
		if (!checkAuth()) {
			toast.error("Please login to add items to cart", {
				position: "top-right",
				autoClose: 3000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true,
				draggable: true,
			});
			throw new Error("User not authenticated");
		}

		try {
			const token = Cookies.get("ecom_user_token");
			const response = await axios.post(
				`${process.env.REACT_APP_BACKEND_URL}/api/buyer/add-to-cart`,
				{ productId, variantId, quantity },
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				},
			);

			const updatedItem = response.data.cart.items.find(
				(item) =>
					item.productId === productId &&
					(variantId ? item.variantId === variantId : true),
			);

			if (updatedItem) {
				const formattedItem = {
					...updatedItem,
					productId: updatedItem.productId,
					variantId: updatedItem.variantId || null,
					variantName: updatedItem.variantId?.name || null,
					quantity: updatedItem.quantity,
				};
				dispatch(addToCartSuccess(formattedItem));
			} else {
				dispatch(removeFromCartSuccess({ productId, variantId }));
			}
		} catch (error) {
			// Check for authentication errors
			if (
				error.response &&
				(error.response.status === 401 || error.response.status === 403)
			) {
				handleAuthError(dispatch);
				return;
			}

			console.error("Error adding to cart:", error);
			throw error;
		}
	};

export const removeFromCart =
	({ productId, variantId }) =>
	async (dispatch) => {
		// ✅ FIRST CHECK: Agar user authenticated nahi hai, to API call hi na karein
		if (!checkAuth()) {
			toast.error("Please login to modify cart", {
				position: "top-right",
				autoClose: 3000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true,
				draggable: true,
			});
			throw new Error("User not authenticated");
		}

		try {
			const token = Cookies.get("ecom_user_token");
			await axios.patch(
				`${process.env.REACT_APP_BACKEND_URL}/api/buyer/remove-from-cart`,
				{ productId, variantId },
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				},
			);

			dispatch(removeFromCartSuccess({ productId, variantId }));
		} catch (error) {
			// Check for authentication errors
			if (
				error.response &&
				(error.response.status === 401 || error.response.status === 403)
			) {
				handleAuthError(dispatch);
				return;
			}

			console.error("Error removing from cart:", error);
			throw error;
		}
	};

export const clearCart = () => async (dispatch) => {
	// ✅ FIRST CHECK: Agar user authenticated nahi hai, to API call hi na karein
	if (!checkAuth()) {
		toast.error("Please login to clear cart", {
			position: "top-right",
			autoClose: 3000,
			hideProgressBar: false,
			closeOnClick: true,
			pauseOnHover: true,
			draggable: true,
		});
		throw new Error("User not authenticated");
	}

	try {
		const token = Cookies.get("ecom_user_token");
		const cartResponse = await axios.get(
			`${process.env.REACT_APP_BACKEND_URL}/api/buyer/get-cart`,
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		);

		const removePromises = cartResponse.data.cart.items.map((item) =>
			axios.patch(
				`${process.env.REACT_APP_BACKEND_URL}/api/buyer/remove-from-cart`,
				{
					productId: item.productId._id,
					variantId: item.variantId?._id,
				},
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				},
			),
		);

		await Promise.all(removePromises);
		dispatch(clearCartSuccess());
	} catch (error) {
		// Check for authentication errors
		if (
			error.response &&
			(error.response.status === 401 || error.response.status === 403)
		) {
			handleAuthError(dispatch);
			return;
		}

		console.error("Error clearing cart:", error);
		throw error;
	}
};

export const updateItemQuantity =
	({ productId, variantId, quantity }) =>
	async (dispatch) => {
		// ✅ FIRST CHECK: Agar user authenticated nahi hai, to API call hi na karein
		if (!checkAuth()) {
			toast.error("Please login to update cart", {
				position: "top-right",
				autoClose: 3000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true,
				draggable: true,
			});
			throw new Error("User not authenticated");
		}

		try {
			const token = Cookies.get("ecom_user_token");

			if (quantity <= 0) {
				// Remove item if quantity is 0 or less
				await axios.patch(
					`${process.env.REACT_APP_BACKEND_URL}/api/buyer/remove-from-cart`,
					{ productId, variantId },
					{
						headers: {
							Authorization: `Bearer ${token}`,
						},
					},
				);
				dispatch(removeFromCartSuccess({ productId, variantId }));
			} else {
				// Optimistically update UI first
				dispatch(
					updateCartItemQuantity({ productId, variantId, quantity }),
				);

				// Then sync with server
				await axios.post(
					`${process.env.REACT_APP_BACKEND_URL}/api/buyer/add-to-cart`,
					{ productId, variantId, quantity },
					{
						headers: {
							Authorization: `Bearer ${token}`,
						},
					},
				);
			}
		} catch (error) {
			// Check for authentication errors
			if (
				error.response &&
				(error.response.status === 401 || error.response.status === 403)
			) {
				handleAuthError(dispatch);
				return;
			}

			console.error("Error updating quantity:", error);
			dispatch(fetchCart()); // Refresh cart to sync with server
			throw error;
		}
	};
